<template>
  <b-modal
    :visible="value"
    :title="notificacaoEdicao ? 'Editar Notificação' : 'Nova Notificação'"
    size="md"
    centered
    scrollable
    body-class="modal-body-compact"
    @hidden="fechar"
  >
    <b-form @submit.prevent="salvar">
      <!-- Status Type -->
      <b-form-group
        label="Tipo de Status"
        label-for="status_type"
        description="Selecione o status que irá disparar esta notificação"
      >
        <b-form-select
          id="status_type"
          v-model="form.status_type"
          :options="statusOptions"
          :state="validacao.status_type"
          :disabled="!!notificacaoEdicao"
          required
        >
          <template #first>
            <b-form-select-option
              :value="null"
              disabled
            >
              Selecione um status
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="validacao.status_type">
          Por favor, selecione um status
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Método de Pagamento (somente para Aguardando Pagamento) -->
      <b-form-group
        v-if="form.status_type === 2"
        label="Método de Pagamento"
        label-for="payment_id"
        description="Selecione o método de pagamento específico"
      >
        <b-form-select
          id="payment_id"
          v-model="form.payment_id"
          :options="metodoPagamentoOptions"
          :state="validacao.payment_id"
          :disabled="!!notificacaoEdicao"
          required
        >
          <template #first>
            <b-form-select-option
              :value="null"
              disabled
            >
              Selecione um método
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="validacao.payment_id">
          Por favor, selecione um método de pagamento
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Template do WhatsApp -->
      <b-form-group
        label="Template do WhatsApp"
        label-for="template_name"
        description="Selecione o template aprovado que será enviado"
      >
        <div class="template-selector">
          <div
            v-if="!form.template_name"
            class="template-empty"
            @click="abrirModalTemplates"
          >
            <feather-icon
              icon="FileTextIcon"
              size="32"
              class="text-muted mb-2"
            />
            <p class="mb-1">
              <strong>Selecionar Template</strong>
            </p>
            <small class="text-muted">Clique para escolher um template aprovado</small>
          </div>
          <div
            v-else
            class="template-selected"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="20"
                  class="text-success mr-2"
                />
                <div>
                  <strong>{{ form.template_name }}</strong>
                  <br>
                  <small class="text-muted">Template selecionado</small>
                </div>
              </div>
              <div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="abrirModalTemplates"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="14"
                    class="mr-50"
                  />
                  Trocar
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="ml-1"
                  @click="removerTemplate"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <b-form-invalid-feedback :state="validacao.template_name">
          Por favor, selecione um template
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Seleção de Variável para Botão URL Dinâmica -->
      <b-form-group
        v-if="temBotaoUrlDinamica"
        label="Variável do Botão URL"
        label-for="url_variable"
        description="Selecione qual variável será usada no botão com link dinâmico"
      >
        <b-form-select
          id="url_variable"
          v-model="form.url_variable"
          :options="urlVariableOptions"
          :state="validacao.url_variable"
          required
        >
          <template #first>
            <b-form-select-option
              :value="null"
              disabled
            >
              Selecione a variável para o link
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="validacao.url_variable">
          Por favor, selecione a variável que será usada no link do botão
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Upload de Mídia (Imagem ou Vídeo) -->
      <b-form-group
        v-if="headerMediaType"
        :label="`Upload de ${headerMediaType === 'IMAGE' ? 'Imagem' : 'Vídeo'}`"
        :label-for="`media_upload_${headerMediaType}`"
      >
        <!-- Indicador de obrigatoriedade -->
        <template #description>
          <span :class="mediaObrigatoria && !temMidia ? 'text-danger font-weight-bold' : ''">
            <feather-icon
              v-if="mediaObrigatoria && !temMidia"
              icon="AlertCircleIcon"
              size="12"
              class="mr-1"
            />
            <strong v-if="mediaObrigatoria">Obrigatório:</strong>
            {{ headerMediaType === 'IMAGE' ? 'Imagem (máx. 5MB)' : 'Vídeo (máx. 16MB)' }} para o cabeçalho
          </span>
        </template>

        <div class="media-upload-area">
          <!-- Área de Upload -->
          <div
            v-if="!mediaPreview"
            class="upload-zone"
            @click="$refs.mediaFileInput.click()"
          >
            <feather-icon
              :icon="headerMediaType === 'IMAGE' ? 'ImageIcon' : 'VideoIcon'"
              size="32"
              class="text-muted mb-2"
            />
            <p class="mb-0 text-muted">
              Clique para selecionar {{ headerMediaType === 'IMAGE' ? 'uma imagem' : 'um vídeo' }}
            </p>
            <small class="text-muted">
              {{ headerMediaType === 'IMAGE' ? 'JPG, PNG (máx. 5MB)' : 'MP4, AVI (máx. 16MB)' }}
            </small>
          </div>

          <!-- Preview da Mídia -->
          <div
            v-else
            class="media-preview-container"
          >
            <!-- Badge indicando se é mídia existente ou nova -->
            <div
              v-if="mediaExistente.header_media_url && !mediaFile"
              class="mb-2"
            >
              <b-badge variant="light-info">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="12"
                  class="mr-50"
                />
                Mídia Atual (do servidor)
              </b-badge>
            </div>
            <div
              v-else-if="mediaFile"
              class="mb-2"
            >
              <b-badge variant="light-success">
                <feather-icon
                  icon="UploadIcon"
                  size="12"
                  class="mr-50"
                />
                Nova Mídia (será enviada)
              </b-badge>
            </div>

            <div class="media-preview">
              <img
                v-if="headerMediaType === 'IMAGE'"
                :src="mediaPreview"
                alt="Preview"
                class="preview-image"
              >
              <video
                v-else-if="headerMediaType === 'VIDEO'"
                :src="mediaPreview"
                controls
                class="preview-video"
              />
            </div>
            <div class="media-actions mt-2">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="removerMedia"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                  class="mr-50"
                />
                Remover
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="$refs.mediaFileInput.click()"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="14"
                  class="mr-50"
                />
                {{ mediaFile ? 'Trocar' : 'Atualizar' }}
              </b-button>
            </div>
          </div>

          <!-- Input File (hidden) -->
          <input
            ref="mediaFileInput"
            type="file"
            :accept="headerMediaType === 'IMAGE' ? 'image/jpeg,image/png,image/jpg' : 'video/mp4,video/avi,video/quicktime'"
            style="display: none"
            @change="handleMediaUpload"
          >

          <!-- Progress Bar -->
          <b-progress
            v-if="uploadProgress > 0 && uploadProgress < 100"
            :value="uploadProgress"
            :max="100"
            variant="primary"
            striped
            animated
            class="mt-2"
          />
        </div>
      </b-form-group>

      <!-- Preview do Template (se selecionado) -->
      <div
        v-if="templateSelecionado"
        ref="templatePreview"
        class="template-preview mt-2"
      >
        <div class="d-flex align-items-center justify-content-between mb-2">
          <h6 class="mb-0">
            <feather-icon
              icon="EyeIcon"
              size="14"
              class="mr-1"
            />
            Preview
          </h6>
          <small class="text-muted">{{ previewTemplateName }}</small>
        </div>

        <div class="whatsapp-preview-simple">
          <!-- Header -->
          <div
            v-if="previewHeader"
            class="preview-header"
          >
            <strong>{{ previewHeader }}</strong>
          </div>

          <!-- Body -->
          <div
            v-if="previewBody"
            class="preview-body"
          >
            {{ previewBody }}
          </div>

          <!-- Botões -->
          <div
            v-if="previewBotoes && previewBotoes.length > 0"
            class="preview-buttons"
          >
            <div
              v-for="(botao, index) in previewBotoes"
              :key="index"
              class="preview-button"
            >
              <feather-icon
                :icon="getBotaoIcon(botao)"
                size="12"
                class="mr-1"
              />
              {{ getBotaoText(botao) }}
            </div>
          </div>
        </div>
      </div>
    </b-form>

    <!-- Modal de Seleção de Templates -->
    <modal-selecao-templates
      v-model="modalTemplatesVisivel"
      :templates="templates"
      :carregando="carregandoTemplates"
      @template-selecionado="onTemplateSelecionado"
    >
      <template #info-text>
        <strong>Apenas templates aprovados</strong> pelo WhatsApp estão disponíveis para uso em notificações.
      </template>
      <template #empty-text>
        Crie templates e aguarde a aprovação do WhatsApp para utilizá-los em notificações.
      </template>
    </modal-selecao-templates>

    <template #modal-footer>
      <b-button
        variant="secondary"
        :disabled="uploadandoMedia"
        @click="fechar"
      >
        Cancelar
      </b-button>
      <b-button
        variant="primary"
        :disabled="!formularioValido || uploadandoMedia"
        @click="salvar"
      >
        <b-spinner
          v-if="uploadandoMedia"
          small
          class="mr-50"
        />
        <feather-icon
          v-else
          icon="SaveIcon"
          size="14"
          class="mr-50"
        />
        {{ uploadandoMedia ? 'Enviando mídia...' : (notificacaoEdicao ? 'Atualizar' : 'Criar') + ' Notificação' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BForm, BFormGroup, BFormSelect, BFormSelectOption, BFormInput, BFormInvalidFeedback, BButton, BSpinner, BProgress, BBadge, BFormCheckbox,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import ModalSelecaoTemplates from '@core/components/modal-selecao-templates/ModalSelecaoTemplates.vue'
import {
  STATUS_NOTIFICACOES, METODOS_PAGAMENTO, buscarTemplatesNotificacao, getPaymentIdPorStatus, uploadMediaNotificacao,
} from '@/functions/basics/notifications-config'

export default {
  name: 'ModalNotificacao',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    BProgress,
    BBadge,
    BFormCheckbox,
    FeatherIcon,
    ModalSelecaoTemplates,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    notificacaoEdicao: {
      type: Object,
      default: null,
    },
    statusPreSelecionado: {
      type: String,
      default: null,
    },
    storeUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: {
        status_type: null,
        payment_id: null,
        template_name: null,
        url_variable: null,
      },
      validacao: {
        status_type: null,
        payment_id: null,
        template_name: null,
        url_variable: null,
      },
      urlVariableOptions: [
        { value: 'link_de_rastreio', text: 'Link de Rastreio' },
        { value: 'link_de_avaliacao', text: 'Link de Avaliação' },
        { value: 'link_do_boleto', text: 'Link do Boleto' },
        { value: 'link_do_carrinho', text: 'Link do Carrinho' },
      ],
      templates: [],
      templateOptions: [],
      carregandoTemplates: false,
      statusDisponiveis: STATUS_NOTIFICACOES,
      modalTemplatesVisivel: false,
      mediaPreview: null,
      mediaFile: null,
      uploadProgress: 0,
      uploadandoMedia: false,
      // Dados de mídia existente (do banco de dados)
      mediaExistente: {
        header_media_id: null,
        header_media_url: null,
        header_media_type: null,
      },
    }
  },
  computed: {
    statusOptions() {
      return this.statusDisponiveis.map(status => ({
        value: status.id,
        text: status.nome,
      }))
    },

    // Opções de métodos de pagamento (PIX e Boleto para Aguardando Pagamento)
    metodoPagamentoOptions() {
      return [
        { value: METODOS_PAGAMENTO.PIX.id, text: METODOS_PAGAMENTO.PIX.nome },
        { value: METODOS_PAGAMENTO.BOLETO.id, text: METODOS_PAGAMENTO.BOLETO.nome },
      ]
    },

    // Retorna todos os templates do WhatsApp Cloud disponíveis
    templatesFiltrados() {
      // Templates do WhatsApp Cloud não são filtrados por status
      // Qualquer template pode ser usado para qualquer status
      return this.templates || []
    },

    templateSelecionado() {
      if (!this.form.template_name) return null
      return this.templates.find(t => (t.template_name_meta || t.name) === this.form.template_name)
    },

    // Verifica se o template tem botão com URL dinâmica (contendo {{1}})
    temBotaoUrlDinamica() {
      if (!this.templateSelecionado) return false

      // Verificar em components (estrutura WhatsApp Cloud API)
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const buttonsComponent = this.templateSelecionado.components.find(c => c.type === 'BUTTONS')
        if (buttonsComponent && buttonsComponent.buttons) {
          return buttonsComponent.buttons.some(btn => btn.type === 'URL' && btn.url && btn.url.includes('{{1}}'))
        }
      }

      // Verificar em botoes (estrutura antiga)
      if (this.templateSelecionado.botoes) {
        const botoes = this.safeParse(this.templateSelecionado.botoes) || []
        return botoes.some(btn => (btn.tipo === 'url' || btn.type === 'URL')
          && (btn.url_variavel || btn.url)
          && (btn.url_variavel?.includes('{{1}}') || btn.url?.includes('{{1}}')))
      }

      return false
    },

    // Preview computed properties
    previewTemplateName() {
      if (!this.templateSelecionado) return ''
      return this.templateSelecionado.template_name_meta || this.templateSelecionado.name || ''
    },

    previewHeader() {
      if (!this.templateSelecionado) return null

      // Tentar pegar header de components (WhatsApp Cloud API)
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const headerComponent = this.templateSelecionado.components.find(c => c.type === 'HEADER')
        if (headerComponent && headerComponent.format === 'TEXT') {
          return headerComponent.text || 'Cabeçalho'
        }
        if (headerComponent && (headerComponent.format === 'IMAGE' || headerComponent.format === 'VIDEO')) {
          return `[${headerComponent.format}]`
        }
      }

      // Tentar estrutura antiga
      const header = this.safeParse(this.templateSelecionado.header)
      if (header && header.variavel) {
        return header.variavel
      }
      if (this.templateSelecionado.header && typeof this.templateSelecionado.header === 'string') {
        return this.templateSelecionado.header
      }

      return null
    },

    previewBody() {
      if (!this.templateSelecionado) return null

      // Tentar pegar body de components (WhatsApp Cloud API)
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const bodyComponent = this.templateSelecionado.components.find(c => c.type === 'BODY')
        if (bodyComponent && bodyComponent.text) {
          return bodyComponent.text
        }
      }

      // Estrutura direta
      if (this.templateSelecionado.body) {
        return this.templateSelecionado.body
      }

      // Fallback para text
      if (this.templateSelecionado.text) {
        return this.templateSelecionado.text
      }

      return null
    },

    previewBotoes() {
      if (!this.templateSelecionado) return []

      // Tentar pegar botões de components (WhatsApp Cloud API)
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const buttonsComponent = this.templateSelecionado.components.find(c => c.type === 'BUTTONS')
        if (buttonsComponent && buttonsComponent.buttons && Array.isArray(buttonsComponent.buttons)) {
          return buttonsComponent.buttons
        }
      }

      // Tentar estrutura antiga
      const botoes = this.safeParse(this.templateSelecionado.botoes)
      if (Array.isArray(botoes)) {
        return botoes
      }

      return []
    },

    // Retorna o tipo de mídia do header (IMAGE ou VIDEO) se houver
    headerMediaType() {
      if (!this.templateSelecionado) return null

      // Verificar na estrutura com components (WhatsApp Cloud API)
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const headerComponent = this.templateSelecionado.components.find(c => c.type === 'HEADER')
        if (headerComponent && (headerComponent.format === 'IMAGE' || headerComponent.format === 'VIDEO')) {
          return headerComponent.format
        }
      }

      // Verificar na estrutura antiga
      const header = this.safeParse(this.templateSelecionado.header)
      if (header && (header.tipo === 'image' || header.tipo === 'video')) {
        return header.tipo.toUpperCase()
      }

      return null
    },

    // Verifica se há mídia (nova upload ou existente do banco)
    temMidia() {
      return !!(this.mediaFile || this.mediaExistente.header_media_url)
    },

    // Verifica se a mídia é obrigatória para o template selecionado
    mediaObrigatoria() {
      return !!this.headerMediaType
    },

    formularioValido() {
      const basico = (
        this.form.status_type
        && this.form.template_name
      )

      // Se for Aguardando Pagamento, exigir payment_id
      if (this.form.status_type === 2) {
        if (!basico || !this.form.payment_id) return false
      } else if (!basico) {
        return false
      }

      // Se o template tiver botão URL dinâmico, exigir url_variable
      if (this.temBotaoUrlDinamica && !this.form.url_variable) {
        return false
      }

      // Se o template exigir mídia no header, verificar se há mídia
      if (this.mediaObrigatoria && !this.temMidia) {
        return false
      }

      return true
    },
  },
  watch: {
    value(visivel) {
      if (visivel) {
        this.inicializarFormulario()
        this.carregarTemplates()
      }
    },

    'form.status_type': function (novoStatus, statusAnterior) {
      // Se mudar de status, limpar payment_id se não for Aguardando Pagamento
      if (novoStatus !== 2) {
        this.form.payment_id = null
      }

      // Atualizar opções de templates
      this.atualizarTemplateOptions()
    },

    'form.template_name': function (novoTemplate) {
      // Quando um template é selecionado, fazer scroll até o preview
      if (novoTemplate) {
        this.$nextTick(() => {
          if (this.$refs.templatePreview) {
            this.$refs.templatePreview.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            })
          }
        })
      }

      // Limpar url_variable ao mudar de template
      // (Exceto se estiver em modo edição e for a primeira carga)
      if (!this.notificacaoEdicao || this.form.url_variable) {
        this.form.url_variable = null
      }

      // Limpar mídia se o novo template não tem header de mídia
      if (!this.headerMediaType) {
        this.removerMedia()
      }
    },
  },
  methods: {
    inicializarFormulario() {
      if (this.notificacaoEdicao) {
        // Modo edição
        console.log('📋 Notificação em edição:', this.notificacaoEdicao)

        // Extrair url_variable dos botões se existir
        let urlVariable = null
        if (this.notificacaoEdicao.botoes) {
          const botoes = this.safeParse(this.notificacaoEdicao.botoes) || []
          const botaoUrl = botoes.find(b => b.tipo === 'url' && b.url_variavel)
          if (botaoUrl && botaoUrl.url_variavel) {
            // Extrair variável entre {{ }} no formato {{nome_variavel}}
            const match = botaoUrl.url_variavel.match(/\{\{([^}]+)\}\}/)
            if (match && match[1]) {
              urlVariable = match[1]
            }
          }
        }

        this.form = {
          ...this.notificacaoEdicao,
          status_type: this.notificacaoEdicao.status_id || this.notificacaoEdicao.status_type, // Mapear status_id para status_type
          template_name: this.notificacaoEdicao.template_name_meta || this.notificacaoEdicao.template_name, // Mapear template_name_meta
          payment_id: this.notificacaoEdicao.payment_id || null,
          url_variable: urlVariable,
        }

        // Carregar mídia existente se houver
        if (this.notificacaoEdicao.header_media_url) {
          this.mediaExistente = {
            header_media_id: this.notificacaoEdicao.header_media_id || null,
            header_media_url: this.notificacaoEdicao.header_media_url || null,
            header_media_type: this.notificacaoEdicao.header_media_type || null,
          }

          // Definir o preview com a mídia existente
          this.mediaPreview = this.notificacaoEdicao.header_media_url

          console.log('📸 Mídia existente carregada:', this.mediaExistente)
        } else {
          // Limpar mídia se não houver
          this.removerMedia()
        }
      } else {
        // Modo criação
        this.form = {
          status_type: this.statusPreSelecionado || null,
          payment_id: null,
          template_name: null,
          url_variable: null,
        }

        // Limpar mídia
        this.removerMedia()
      }

      // Resetar validação
      this.validacao = {
        status_type: null,
        payment_id: null,
        template_name: null,
        url_variable: null,
      }
    },

    async carregarTemplates() {
      this.carregandoTemplates = true
      try {
        const templates = await buscarTemplatesNotificacao(this.storeUuid)

        this.templates = templates || []

        console.log('📋 Templates carregados:', this.templates.length)

        // Atualizar opções de templates após carregar
        this.$nextTick(() => {
          this.atualizarTemplateOptions()
        })

        if (this.templates.length === 0) {
          this.$bvToast.toast('Nenhum template do WhatsApp disponível para esta loja', {
            title: 'Aviso',
            variant: 'warning',
            solid: true,
          })
        }
      } catch (error) {
        console.error('Erro ao carregar templates:', error)
        this.$bvToast.toast('Erro ao carregar templates do WhatsApp', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.templates = []
        this.templateOptions = []
      } finally {
        this.carregandoTemplates = false
      }
    },

    // Abrir modal de seleção visual de templates
    abrirModalTemplates() {
      this.modalTemplatesVisivel = true
    },

    // Callback quando um template é selecionado no modal
    onTemplateSelecionado(templateData) {
      console.log('📋 Template selecionado:', templateData)
      this.form.template_name = templateData.name

      this.$bvToast.toast(`Template "${templateData.name}" selecionado!`, {
        title: 'Template Selecionado',
        variant: 'success',
        solid: true,
      })

      // Fazer scroll até o preview
      this.$nextTick(() => {
        if (this.$refs.templatePreview) {
          this.$refs.templatePreview.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          })
        }
      })
    },

    // Remover template selecionado
    removerTemplate() {
      this.form.template_name = null
      this.form.url_variable = null
      this.removerMedia()

      this.$bvToast.toast('Template removido', {
        title: 'Template',
        variant: 'info',
        solid: true,
      })
    },

    // Atualiza opções de templates quando o status muda
    atualizarTemplateOptions() {
      // Mapear todos os templates do WhatsApp Cloud disponíveis
      this.templateOptions = this.templatesFiltrados.map(template => ({
        value: template.template_name_meta || template.name,
        text: template.template_name_meta || template.name,
      }))

      console.log('📝 Template options atualizadas:', this.templateOptions.length, this.templateOptions)

      // Limpar template selecionado se não estiver nas opções
      if (this.form.template_name) {
        const templateValido = this.templateOptions.find(opt => opt.value === this.form.template_name)
        if (!templateValido) {
          this.form.template_name = null
        }
      }
    },

    validarFormulario() {
      let valido = true

      // Validar status_type
      if (!this.form.status_type) {
        this.validacao.status_type = false
        valido = false
      } else {
        this.validacao.status_type = true
      }

      // Validar payment_id (obrigatório para Aguardando Pagamento)
      if (this.form.status_type === 2 && !this.form.payment_id) {
        this.validacao.payment_id = false
        valido = false
      } else {
        this.validacao.payment_id = true
      }

      // Validar template_name
      if (!this.form.template_name) {
        this.validacao.template_name = false
        valido = false
      } else {
        this.validacao.template_name = true
      }

      // Validar url_variable (obrigatório se template tiver botão com URL dinâmica)
      if (this.temBotaoUrlDinamica && !this.form.url_variable) {
        this.validacao.url_variable = false
        valido = false
      } else {
        this.validacao.url_variable = true
      }

      return valido
    },

    // Handle de upload de mídia
    handleMediaUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const maxSize = this.headerMediaType === 'IMAGE' ? 5 * 1024 * 1024 : 16 * 1024 * 1024 // 5MB ou 16MB
      const tiposPermitidos = this.headerMediaType === 'IMAGE'
        ? ['image/jpeg', 'image/jpg', 'image/png']
        : ['video/mp4', 'video/avi', 'video/quicktime']

      // Validar tipo de arquivo
      if (!tiposPermitidos.includes(file.type)) {
        this.$bvToast.toast(
          `Tipo de arquivo inválido. Envie apenas ${this.headerMediaType === 'IMAGE' ? 'imagens (JPG, PNG)' : 'vídeos (MP4, AVI)'}`,
          {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          },
        )
        return
      }

      // Validar tamanho
      if (file.size > maxSize) {
        const maxSizeMB = this.headerMediaType === 'IMAGE' ? '5MB' : '16MB'
        this.$bvToast.toast(
          `O arquivo excede o tamanho máximo de ${maxSizeMB}`,
          {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          },
        )
        return
      }

      // Criar preview
      this.mediaFile = file
      const reader = new FileReader()

      reader.onload = e => {
        this.mediaPreview = e.target.result
      }

      reader.onprogress = e => {
        if (e.lengthComputable) {
          this.uploadProgress = Math.round((e.loaded / e.total) * 100)
        }
      }

      reader.onloadend = () => {
        this.uploadProgress = 0
      }

      reader.readAsDataURL(file)

      this.$bvToast.toast(
        `${this.headerMediaType === 'IMAGE' ? 'Imagem' : 'Vídeo'} carregado com sucesso!`,
        {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        },
      )
    },

    // Remover mídia
    removerMedia() {
      this.mediaFile = null
      this.mediaPreview = null
      this.uploadProgress = 0

      // Limpar mídia existente também
      this.mediaExistente = {
        header_media_id: null,
        header_media_url: null,
        header_media_type: null,
      }

      if (this.$refs.mediaFileInput) {
        this.$refs.mediaFileInput.value = ''
      }
    },

    async salvar() {
      if (!this.validarFormulario()) {
        this.$bvToast.toast('Por favor, preencha todos os campos obrigatórios', {
          title: 'Validação',
          variant: 'warning',
          solid: true,
        })
        return
      }

      // Verificar se há template selecionado
      if (!this.templateSelecionado) {
        this.$bvToast.toast('Erro: nenhum template selecionado', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        return
      }

      // Preparar dados com payment_id correto
      const dadosParaSalvar = { ...this.form }

      // Definir delay_minutes como 0 (envio imediato) e ativo como 1 (sempre ativa)
      dadosParaSalvar.delay_minutes = 0
      dadosParaSalvar.ativo = 1

      // Definir payment_id baseado no status
      if (this.form.status_type === 2) {
        // Aguardando Pagamento: usar o payment_id selecionado (PIX ou Boleto)
        dadosParaSalvar.payment_id = this.form.payment_id
      } else {
        // Outros status: usar getPaymentIdPorStatus
        dadosParaSalvar.payment_id = getPaymentIdPorStatus(this.form.status_type)
      }

      // Adicionar dados completos do template selecionado
      console.log('📋 Template selecionado completo:', this.templateSelecionado)

      // Obter body do template
      let body = null
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const bodyComponent = this.templateSelecionado.components.find(c => c.type === 'BODY')
        body = bodyComponent ? bodyComponent.text : null
      }

      // Fallback para body direto se não vier de components
      if (!body && this.templateSelecionado.body) {
        body = this.templateSelecionado.body
      }

      // Se ainda não tem body, usar o texto do template
      if (!body && this.templateSelecionado.text) {
        body = this.templateSelecionado.text
      }

      console.log('📝 Body extraído:', body)

      if (!body) {
        this.$bvToast.toast('Erro: não foi possível extrair o conteúdo do template', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        return
      }

      // Obter header
      let headerData = null
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const headerComponent = this.templateSelecionado.components.find(c => c.type === 'HEADER')
        headerData = headerComponent
      } else if (this.templateSelecionado.header) {
        headerData = this.safeParse(this.templateSelecionado.header) || this.templateSelecionado.header
      }

      // Obter botões
      let botoesData = []
      if (this.templateSelecionado.components && Array.isArray(this.templateSelecionado.components)) {
        const buttonsComponent = this.templateSelecionado.components.find(c => c.type === 'BUTTONS')
        botoesData = buttonsComponent && buttonsComponent.buttons ? buttonsComponent.buttons : []
      } else if (this.templateSelecionado.botoes) {
        botoesData = this.safeParse(this.templateSelecionado.botoes) || []
      }

      // Adicionar dados completos do template para a API
      dadosParaSalvar.template_name_meta = this.templateSelecionado.template_name_meta || this.templateSelecionado.name
      dadosParaSalvar.linguagem = this.templateSelecionado.linguagem || this.templateSelecionado.language || 'pt_BR'
      dadosParaSalvar.body = body

      // Mapear variáveis a partir do body do template (POST /api/templates)
      dadosParaSalvar.variaveis = this.mapearVariaveis(body)

      // Processar botões
      dadosParaSalvar.botoes = this.processarBotoes(botoesData)

      // Processar header
      dadosParaSalvar.header = this.processarHeader(headerData, body)

      // Verificar se todos os campos necessários estão presentes
      if (!dadosParaSalvar.template_name_meta || !dadosParaSalvar.body) {
        this.$bvToast.toast('Erro: falha ao mapear dados do template', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        console.error('❌ Dados incompletos:', dadosParaSalvar)
        return
      }

      // Gerenciar mídia (nova ou existente)
      if (this.mediaFile && this.headerMediaType) {
        // Caso 1: Há nova mídia para upload
        try {
          this.uploadandoMedia = true

          // Determinar o tipo de mídia (image ou video)
          const mediaType = this.headerMediaType.toLowerCase()

          console.log(`📤 Fazendo upload de ${mediaType}...`)

          // Fazer upload da mídia e obter os dados completos
          const mediaData = await uploadMediaNotificacao(this.mediaFile, this.storeUuid, mediaType)

          console.log('✅ Upload concluído!', mediaData)

          // Adicionar dados completos da mídia ao payload
          dadosParaSalvar.header_media_id = mediaData.header_media_id
          dadosParaSalvar.header_media_url = mediaData.header_media_url
          dadosParaSalvar.header_media_type = mediaData.header_media_type

          this.$bvToast.toast(
            `${mediaType === 'image' ? 'Imagem' : 'Vídeo'} enviado com sucesso!`,
            {
              title: 'Upload Concluído',
              variant: 'success',
              solid: true,
            },
          )
        } catch (error) {
          console.error('❌ Erro ao fazer upload da mídia:', error)
          this.uploadandoMedia = false

          this.$bvToast.toast(
            'Erro ao fazer upload da mídia. Tente novamente.',
            {
              title: 'Erro no Upload',
              variant: 'danger',
              solid: true,
            },
          )
          return
        } finally {
          this.uploadandoMedia = false
        }
      } else if (this.mediaExistente.header_media_url && this.headerMediaType) {
        // Caso 2: Não há nova mídia, mas há mídia existente (manter a mídia atual)
        console.log('📸 Mantendo mídia existente:', this.mediaExistente)

        dadosParaSalvar.header_media_id = this.mediaExistente.header_media_id
        dadosParaSalvar.header_media_url = this.mediaExistente.header_media_url
        dadosParaSalvar.header_media_type = this.mediaExistente.header_media_type
      }

      // Debug: mostrar dados completos antes de emitir
      console.log('✅ Dados COMPLETOS a serem enviados:', JSON.stringify(dadosParaSalvar, null, 2))

      // Emitir dados completos
      this.$emit('salvar', dadosParaSalvar)
    },

    fechar() {
      this.$emit('input', false)
    },

    // Helpers para preview
    safeParse(value) {
      if (!value) return null
      if (typeof value === 'object') return value
      try {
        return JSON.parse(value)
      } catch (e) {
        return null
      }
    },

    getBotaoIcon(botao) {
      // WhatsApp Cloud API structure
      if (botao.type === 'URL' || botao.tipo === 'url') {
        return 'ExternalLinkIcon'
      }
      if (botao.type === 'PHONE_NUMBER') {
        return 'PhoneIcon'
      }
      if (botao.type === 'QUICK_REPLY' || botao.tipo === 'quick_reply') {
        return 'MessageCircleIcon'
      }
      return 'MessageCircleIcon'
    },

    getBotaoText(botao) {
      // WhatsApp Cloud API structure
      return botao.text || botao.texto || 'Botão'
    },

    // Extrai nomes de variáveis do texto (tudo entre {{ }})
    extrairVariaveis(texto) {
      if (!texto) {
        console.log('⚠️ extrairVariaveis: texto vazio ou null')
        return []
      }

      console.log(`🔎 Extraindo variáveis de: "${texto}"`)
      // Regex para capturar nomes de variáveis: {{nome_variavel}}
      const regex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g
      const variaveis = []
      let match

      while ((match = regex.exec(texto)) !== null) {
        const nomeVariavel = match[1]
        console.log(`  🔢 Encontrado: {{${nomeVariavel}}}`)
        if (!variaveis.includes(nomeVariavel)) {
          variaveis.push(nomeVariavel)
        }
      }

      console.log(`  ✅ Variáveis extraídas: [${variaveis.join(', ')}]`)
      return variaveis
    },

    // Mapeia variáveis encontradas no body (não considera header para o array enviado à API)
    mapearVariaveis(body) {
      console.log('🔍 Mapeamento de variáveis (somente body)')
      console.log('📝 Body:', body)

      const variaveisPermitidas = {
        primeiro_nome: { nome: 'primeiro_nome', descricao: 'Primeiro nome do cliente' },
        email: { nome: 'email', descricao: 'E-mail do cliente' },
        numero_pedido: { nome: 'numero_pedido', descricao: 'Número do pedido' },
        valor_total: { nome: 'valor_total', descricao: 'Valor total' },
        link_do_boleto: { nome: 'link_do_boleto', descricao: 'Link do boleto' },
        codigo_do_boleto: { nome: 'codigo_do_boleto', descricao: 'Código do boleto' },
        link_de_rastreio: { nome: 'link_de_rastreio', descricao: 'Link de rastreio' },
        codigo_de_rastreio: { nome: 'codigo_de_rastreio', descricao: 'Código de rastreio' },
        link_de_avaliacao: { nome: 'link_de_avaliacao', descricao: 'Link de avaliação' },
        link_do_carrinho: { nome: 'link_do_carrinho', descricao: 'Link do carrinho' },
        data_entrega: { nome: 'data_entrega', descricao: 'Data de entrega' },
        total_pedido: { nome: 'total_pedido', descricao: 'Valor total do pedido' },
        saldo: { nome: 'saldo', descricao: 'Saldo' },
        data_expiracao: { nome: 'data_expiracao', descricao: 'Data de expiração' },
        data_envio: { nome: 'data_envio', descricao: 'Data de envio' },
        cashback: { nome: 'cashback', descricao: 'Valor do cashback' },
        data_cashback_expirar: { nome: 'data_cashback_expirar', descricao: 'Data de expiração do cashback' },
        link_rastreio: { nome: 'link_rastreio', descricao: 'Link de rastreamento' },
        link_avaliacao: { nome: 'link_avaliacao', descricao: 'Link de avaliação' },
        link_boleto: { nome: 'link_boleto', descricao: 'Link do boleto' },
        codigo_boleto: { nome: 'codigo_boleto', descricao: 'Código do boleto' },
        pix_qrcode: { nome: 'pix_qrcode', descricao: 'QR Code do PIX' },
      }

      const variaveisBody = this.extrairVariaveis(body)
      console.log('🔢 Variáveis no body:', variaveisBody)

      const todasVariaveis = [...new Set(variaveisBody)]
      console.log('📊 Únicas:', todasVariaveis)

      // Mapear cada variável encontrada para o formato esperado
      const resultado = []
      todasVariaveis.forEach(nomeVar => {
        if (variaveisPermitidas[nomeVar]) {
          resultado.push(variaveisPermitidas[nomeVar])
          console.log(`  ✅ {{${nomeVar}}} → mapeado`)
        } else {
          console.warn(`  ⚠️ {{${nomeVar}}} não é uma variável permitida`)
        }
      })

      console.log('✅ Resultado final do mapeamento:', resultado)
      return resultado
    },

    // Processa botões para o formato esperado
    processarBotoes(botoes) {
      if (!botoes || (Array.isArray(botoes) && botoes.length === 0)) {
        return []
      }

      // Se já for array de objetos, processar
      if (Array.isArray(botoes)) {
        return botoes.map(botao => {
          // WhatsApp Cloud API structure
          if (botao.type || botao.tipo) {
            let urlVariavel = botao.url || botao.url_variavel || null

            // Se for botão URL e tiver {{1}} e houver variável selecionada
            if ((botao.type === 'URL' || botao.tipo === 'url')
                && urlVariavel
                && urlVariavel.includes('{{1}}')
                && this.form.url_variable) {
              // Substituir {{1}} pela variável selecionada
              urlVariavel = urlVariavel.replace('{{1}}', `{{${this.form.url_variable}}}`)
              console.log(`🔗 URL dinâmica atualizada: ${botao.url} → ${urlVariavel}`)
            }

            return {
              tipo: (botao.type || botao.tipo).toLowerCase(),
              texto: botao.text || botao.texto || '',
              url_variavel: urlVariavel,
            }
          }
          return botao
        })
      }

      return []
    },

    // Processa header para o formato esperado
    processarHeader(header, body) {
      if (!header) return null

      // Se header vem de components (WhatsApp Cloud API)
      if (this.templateSelecionado && this.templateSelecionado.components) {
        const headerComponent = this.templateSelecionado.components.find(c => c.type === 'HEADER')

        if (headerComponent) {
          if (headerComponent.format === 'TEXT') {
            // Extrair variável do texto se houver
            const variaveis = this.extrairVariaveis(headerComponent.text || '')
            if (variaveis.length > 0) {
              return {
                tipo: 'text',
                variavel: 'titulo', // Nome padrão para variável do header
              }
            }
            return {
              tipo: 'text',
              texto: headerComponent.text || '',
            }
          } if (headerComponent.format === 'IMAGE') {
            return {
              tipo: 'image',
              variavel: 'imagem_url',
            }
          } if (headerComponent.format === 'VIDEO') {
            return {
              tipo: 'video',
              variavel: 'video_url',
            }
          } if (headerComponent.format === 'DOCUMENT') {
            return {
              tipo: 'document',
              variavel: 'documento_url',
            }
          }
        }
      }

      // Estrutura antiga
      if (typeof header === 'object' && header.variavel) {
        return {
          tipo: header.tipo || 'text',
          variavel: header.variavel,
        }
      }

      return null
    },
  },
}
</script>

<style scoped>
/* Modal Body Compacto */
::v-deep .modal-body-compact {
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
  scroll-behavior: smooth;
}

/* Template Selector */
.template-selector {
  margin-bottom: 0.5rem;
}

.template-empty {
  border: 2px dashed #d8d6de;
  border-radius: 12px;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.template-empty:hover {
  border-color: #7367f0;
  background: #f8f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15);
}

.template-selected {
  border: 2px solid #28c76f;
  border-radius: 12px;
  padding: 1rem;
  background: #f0fff4;
  transition: all 0.3s ease;
}

.template-selected:hover {
  box-shadow: 0 4px 12px rgba(40, 199, 111, 0.2);
}

/* Preview do Template */
.template-preview {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  scroll-margin-top: 1rem;
  transition: all 0.3s ease;
}

.whatsapp-preview-simple {
  background: #ffffff;
  border-radius: 6px;
  padding: 0.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  max-height: 200px;
  overflow-y: auto;
}

.preview-header {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #075e54;
  font-weight: 600;
}

.preview-body {
  font-size: 0.85rem;
  line-height: 1.4;
  color: #303030;
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.preview-buttons {
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.preview-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.6rem;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #075e54;
  transition: background 0.2s;
}

.preview-button:hover {
  background: #e0e0e0;
}

/* Scrollbar customizado para o preview */
.whatsapp-preview-simple::-webkit-scrollbar {
  width: 4px;
}

.whatsapp-preview-simple::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.whatsapp-preview-simple::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

.whatsapp-preview-simple::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Upload de Mídia */
.media-upload-area {
  margin-top: 0.5rem;
}

.upload-zone {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.upload-zone:hover {
  border-color: #7367f0;
  background: #f8f7ff;
}

.media-preview-container {
  position: relative;
}

.media-preview {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.preview-image,
.preview-video {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  display: block;
}

.media-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}
</style>
